import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import acApi from 'axios/ac/acApi';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import { buildAuditLogParams } from './auditLogQuery';
import { AuditLogDtoApi, AuditLogFilters, AuditLogRow, AuditLogSort, PageSize } from './types';

export interface UseAuditLogResult {
  rows: AuditLogRow[];
  total: number;
  isLoading: boolean;
  error: Error | null;
}

const toRow = (dto: AuditLogDtoApi, workspaceNamesById: Map<number, string>): AuditLogRow => ({
  id: dto.id,
  // occurred_at is a Unix epoch in seconds; convert to an ISO string for the UI.
  occurredAt: new Date(dto.occurred_at * 1000).toISOString(),
  eventType: dto.event_type,
  workspaceId: dto.workspace_id,
  workspaceName: workspaceNamesById.get(dto.workspace_id) ?? String(dto.workspace_id),
  userId: dto.user_id,
  userEmail: dto.user_email,
  message: dto.message,
});

export const useAuditLog = (
  filters: AuditLogFilters,
  page: number,
  pageSize: PageSize,
  sort: AuditLogSort,
  refreshKey: number = 0,
): UseAuditLogResult => {
  const workspaces = useSelector(
    (state: ApplicationState) => state.blazeUserConfig.blazeUserConfig?.user?.workspaces ?? [],
  );
  const workspaceNamesById = useMemo(() => new Map(workspaces.map(w => [w.id, w.name] as const)), [workspaces]);

  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    const params = buildAuditLogParams(filters, page, pageSize, sort);
    const request =
      filters.workspaceId == null
        ? acApi.auditLog.getAccountAuditLog(params)
        : acApi.auditLog.getWorkspaceAuditLog(filters.workspaceId, params);
    request
      .then(response => {
        if (cancelled) return;
        const envelope = response.data as BlazeMeterApiResponse;
        const result = (envelope.result as AuditLogDtoApi[]) ?? [];
        setRows(result.map(dto => toRow(dto, workspaceNamesById)));
        setTotal(envelope.total ?? 0);
        setIsLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(err);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, page, pageSize, sort, workspaceNamesById, refreshKey]);

  return { rows, total, isLoading, error };
};
